/**
 * <ProfileCard /> — self-service "Your Profile" card.
 *
 * Sits at the top of the Settings page. Lets the logged-in user edit
 * their own display name. Email + password go through their own flows.
 *
 * Backend contract:
 *   PATCH /api/auth/me  body { name: string }  →  { user: {...} }
 */
import { useEffect, useState } from "react";
import { User, Save, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ProfileCard() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        const u = data?.user || {};
        if (!alive) return;
        setName(u.name || "");
        setEmail(u.email || "");
        setInitial(u.name || u.email || "");
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    const clean = name.trim();
    if (clean.length < 1) {
      toast.error("Name can't be blank");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: clean });
      setInitial(clean);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      toast.success("Your name has been updated ✓");
      // Nudge any sidebar that reads from a global user store to refresh.
      try { window.dispatchEvent(new CustomEvent("user:updated")); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const initialLetter = (initial || "?")[0]?.toUpperCase() || "?";

  return (
    <div className="card mb-6" data-testid="settings-profile-card">
      <div className="label-eyebrow flex items-center gap-1.5 mb-3">
        <User size={11} /> Your profile
      </div>
      <div className="flex items-start gap-4">
        <div
          className="w-16 h-16 shrink-0 rounded-full bg-emerald-600 text-white flex items-center justify-center font-display font-extrabold text-2xl"
          aria-hidden
        >
          {initialLetter}
        </div>
        <div className="flex-1 space-y-2.5">
          <div>
            <label
              className="block text-[11px] font-bold uppercase tracking-wide text-[var(--ink-2)] mb-1"
              htmlFor="profile-name-input"
            >
              Display name
            </label>
            <input
              id="profile-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Tracy Perrin"
              className="w-full px-3 py-2 rounded-lg border-2 border-stone-200 focus:border-emerald-500 focus:outline-none font-sans text-sm bg-white"
              data-testid="settings-profile-name-input"
              maxLength={120}
            />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--ink-2)] mb-1">
              Email
            </div>
            <div
              className="text-sm text-[var(--ink-2)] px-3 py-2 rounded-lg bg-stone-50 border border-stone-200 select-all"
              data-testid="settings-profile-email"
            >
              {email || "—"}
            </div>
            <div className="text-[10px] text-[var(--ink-3)] mt-1">
              Contact support to change your email address.
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold text-sm transition"
              data-testid="settings-profile-save"
            >
              {saving ? (
                <><Loader2 size={13} className="animate-spin" /> Saving…</>
              ) : (
                <><Save size={13} /> Save name</>
              )}
            </button>
            {savedFlash && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700"
                data-testid="settings-profile-saved-flash"
              >
                <CheckCircle2 size={12} /> Saved
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
